import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { QrCode, Keyboard, CheckCircle, X, AlertCircle, BookOpen, Loader2 } from 'lucide-react';

interface QRScannerProps {
  onScan: (data: string) => void;
  onError?: (error: string) => void;
  isOpen: boolean;
  onClose: () => void;
}

interface BookInfo {
  title?: string;
  subtitle?: string;
  authors?: string[];
  publisher?: string;
  publication_date?: string;
  isbn?: string;
  description?: string;
  pages?: number;
  language?: string;
  genres?: string[];
  cover_image?: string;
}

export const QRScanner: React.FC<QRScannerProps> = ({
  onScan,
  onError,
  isOpen,
  onClose
}) => {
  const [isListening, setIsListening] = useState(false);
  const [scannedData, setScannedData] = useState('');
  const [inputBuffer, setInputBuffer] = useState('');
  const [scanHistory, setScanHistory] = useState<string[]>([]);
  const [isFetchingBook, setIsFetchingBook] = useState(false);
  const [bookInfo, setBookInfo] = useState<BookInfo | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const useBookButtonRef = useRef<HTMLButtonElement>(null);
  const bufferTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Extract ISBN from scanned data
  const extractISBN = (data: string): string | null => {
    // Helper validators
    const isValidISBN13 = (s: string) => {
      if (!/^\d{13}$/.test(s)) return false;
      const digits = s.split('').map(Number);
      const sum = digits.reduce((acc, d, i) => acc + d * (i % 2 === 0 ? 1 : 3), 0);
      return sum % 10 === 0;
    };

    const isValidISBN10 = (s: string) => {
      if (!/^[0-9]{9}[0-9Xx]$/.test(s)) return false;
      const chars = s.split('');
      let sum = 0;
      for (let i = 0; i < 9; i++) sum += (10 - i) * parseInt(chars[i], 10);
      const check = chars[9].toUpperCase() === 'X' ? 10 : parseInt(chars[9], 10);
      sum += check;
      return sum % 11 === 0;
    };

    // Quick JSON parse path
    try {
      const parsed = JSON.parse(data);
      if (parsed.isbn && typeof parsed.isbn === 'string') return parsed.isbn;
      if (parsed.isbn13 && typeof parsed.isbn13 === 'string') return parsed.isbn13;
      if (parsed.isbn10 && typeof parsed.isbn10 === 'string') return parsed.isbn10;
    } catch {
      // Not JSON, continue
    }

    // Remove non ISBN characters except X (possible ISBN-10 check digit)
    const cleaned = data.replace(/[^0-9Xx]/g, '');

    // Search for valid ISBN-13 substrings first
    if (cleaned.length >= 13) {
      for (let i = 0; i <= cleaned.length - 13; i++) {
        const candidate = cleaned.slice(i, i + 13);
        if (isValidISBN13(candidate)) return candidate;
      }
      // fallback: any 13-digit sequence
      const any13 = cleaned.match(/(\d{13})/);
      if (any13) return any13[1];
    }

    // Then try ISBN-10
    if (cleaned.length >= 10) {
      for (let i = 0; i <= cleaned.length - 10; i++) {
        const candidate = cleaned.slice(i, i + 10);
        if (isValidISBN10(candidate)) return candidate;
      }
      const any10 = cleaned.match(/([0-9]{9}[0-9Xx])/);
      if (any10) return any10[1];
    }

    // As a last resort, try loose patterns in the raw string
    const loose13 = data.match(/(\d[\d\- ]{11,}\d)/);
    if (loose13) {
      const digits = loose13[1].replace(/[^0-9]/g, '');
      if (digits.length >= 13) return digits.slice(0, 13);
    }

    return null;
  };

  // Fetch book information from Open Library API
  const fetchBookInfo = async (isbn: string): Promise<BookInfo | null> => {
    try {
      setIsFetchingBook(true);

      // Try the Open Library Books API first (jscmd=data returns the fullest record)
      // Documentation: https://openlibrary.org/dev/docs/api/books
      try {
        const olBooksResp = await fetch(
          `https://openlibrary.org/api/books?bibkeys=ISBN:${isbn}&format=json&jscmd=data`
        );
        if (olBooksResp.ok) {
          const olBooksJson = await olBooksResp.json();
          const key = `ISBN:${isbn}`;
          const rec = olBooksJson[key];
          if (rec) {
            // Extract authors
            const authors = rec.authors?.map((a: any) => a.name) || undefined;

            // Extract publisher
            const publisher = rec.publishers?.[0]?.name || rec.publishers?.[0];

            // Extract description from multiple sources (excerpts preferred, then notes)
            let description: string | undefined;
            if (rec.excerpts && rec.excerpts.length > 0) {
              description = rec.excerpts[0].text;
            } else if (rec.notes) {
              description = typeof rec.notes === 'string' ? rec.notes : rec.notes?.value;
            }

            // Extract subjects/genres from multiple fields
            let subjects: string[] = [];
            if (rec.subjects && Array.isArray(rec.subjects)) {
              subjects = rec.subjects.map((s: any) => {
                return typeof s === 'string' ? s : (s.name || '');
              }).filter(Boolean);
            }
            // Add subject_places and subject_people if available
            if (rec.subject_places && Array.isArray(rec.subject_places)) {
              const places = rec.subject_places.map((s: any) => typeof s === 'string' ? s : s.name).filter(Boolean);
              subjects.push(...places);
            }
            if (rec.subject_people && Array.isArray(rec.subject_people)) {
              const people = rec.subject_people.map((s: any) => typeof s === 'string' ? s : s.name).filter(Boolean);
              subjects.push(...people);
            }

            return {
              title: rec.title,
              subtitle: rec.subtitle,
              authors,
              publisher,
              publication_date: rec.publish_date,
              isbn: isbn,
              description,
              pages: rec.number_of_pages,
              language: rec.languages?.[0]?.key?.replace('/languages/', ''),
              genres: subjects?.slice(0, 3),
              cover_image: rec.cover?.medium || rec.cover?.small || rec.cover?.large
            };
          }
        }
      } catch (err) {
        console.warn('Open Library Books API lookup failed, falling back to ISBN endpoint', err);
      }

      // Final fallback: try the ISBN JSON endpoint (older style)
      const openLibraryResponse = await fetch(`https://openlibrary.org/isbn/${isbn}.json`);
      if (openLibraryResponse.ok) {
        const openLibraryData = await openLibraryResponse.json();

        // If there's a works reference, fetch additional data as before
        let authors: string[] = [];
        let description = '';
        let subjects: string[] = [];

        if (openLibraryData.works && openLibraryData.works.length > 0) {
          try {
            const workResponse = await fetch(`https://openlibrary.org${openLibraryData.works[0].key}.json`);
            if (workResponse.ok) {
              const workData = await workResponse.json();

              if (workData.authors && Array.isArray(workData.authors)) {
                const resolvedAuthors = await Promise.all(
                  workData.authors.map(async (a: any) => {
                    if (a.name) return a.name;
                    const authorKey = a.author?.key || a.key;
                    if (authorKey) {
                      try {
                        const authorResp = await fetch(`https://openlibrary.org${authorKey}.json`);
                        if (authorResp.ok) {
                          const authorData = await authorResp.json();
                          return authorData.name || authorData.full_name || null;
                        }
                      } catch (err) {
                        console.warn('Failed to fetch OpenLibrary author', authorKey, err);
                      }
                    }
                    return null;
                  })
                );

                authors = (resolvedAuthors.filter(Boolean) as string[]).slice(0, 5);
              }

              description = typeof workData.description === 'string'
                ? workData.description
                : workData.description?.value || '';

              subjects = workData.subjects?.slice(0, 3) || [];
            }
          } catch (error) {
            console.error('Error fetching work data:', error);
          }
        }

        return {
          title: openLibraryData.title,
          authors: authors.length > 0 ? authors : undefined,
          publisher: openLibraryData.publishers?.[0],
          publication_date: openLibraryData.publish_date,
          isbn: openLibraryData.isbn_13?.[0] || openLibraryData.isbn_10?.[0] || isbn,
          description: description || openLibraryData.description?.value || openLibraryData.description,
          pages: openLibraryData.number_of_pages,
          language: openLibraryData.languages?.[0]?.key?.replace('/languages/', ''),
          genres: subjects.length > 0 ? subjects : openLibraryData.subjects?.slice(0, 3)
        };
      }

      return null;
    } catch (error) {
      console.error('Error fetching book info:', error);
      return null;
    } finally {
      setIsFetchingBook(false);
    }
  };

  // Clear input buffer after a delay (QR scanners send data quickly)
  const clearBufferTimeout = useCallback(() => {
    if (bufferTimeoutRef.current) {
      clearTimeout(bufferTimeoutRef.current);
    }
    bufferTimeoutRef.current = setTimeout(() => {
      (async () => {
        if (inputBuffer.trim()) {
          const finalData = inputBuffer.trim();
          await processFinalData(finalData);
          setInputBuffer('');
        }
      })();
    }, 700); // 700ms delay to capture complete QR code data (reduce truncation)
  }, [inputBuffer, onScan]);

  // Centralized final data processing (used on Enter and timeout)
  const processFinalData = async (finalData: string) => {
    try {
      console.debug('processFinalData:', finalData);
      setScannedData(finalData);
      setScanHistory(prev => [finalData, ...prev.slice(0, 4)]);

      // Try to extract ISBN and fetch book info
      const isbn = extractISBN(finalData);
      console.debug('extracted isbn:', isbn);
      if (isbn) {
        const fetchedBookInfo = await fetchBookInfo(isbn);
        if (fetchedBookInfo) {
          setBookInfo(fetchedBookInfo);
          // Focus the "Use This Book" button after a short delay
          setTimeout(() => {
            useBookButtonRef.current?.focus();
          }, 100);
          return;
        }
      }

      // If no ISBN or fetch failed, use raw data
      onScan(finalData);
    } catch (err) {
      console.error('processFinalData error:', err);
      onScan(finalData);
    }
  };

  // Handle keyboard input from QR scanner
  const handleKeyDown = useCallback(async (event: KeyboardEvent) => {
    if (!isListening) return;

    // If book info is displayed and user presses Enter, use the book info
    if (event.key === 'Enter' && bookInfo && !inputBuffer.trim()) {
      event.preventDefault();
      handleSelectBookInfo();
      return;
    }

    // QR scanners typically send data with Enter key at the end
    if (event.key === 'Enter') {
      event.preventDefault();
      // If we have buffered input, process it; otherwise, if bookInfo is present
      // the earlier branch will call useBookInfo and return.
      if (inputBuffer.trim()) {
        const finalData = inputBuffer.trim();
        // clear any pending timeout to avoid double-processing
        if (bufferTimeoutRef.current) {
          clearTimeout(bufferTimeoutRef.current);
        }
        await processFinalData(finalData);
        setInputBuffer('');
      }
    } else if (event.key.length === 1) {
      // Only capture printable characters
      setInputBuffer(prev => prev + event.key);
      clearBufferTimeout();
    }
  }, [isListening, inputBuffer, onScan, clearBufferTimeout, bookInfo]);

  useEffect(() => {
    if (isOpen) {
      setIsListening(true);
      setInputBuffer('');
      setScannedData('');
      setBookInfo(null);
      // Focus the hidden input to capture scanner input
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else {
      setIsListening(false);
      setInputBuffer('');
      if (bufferTimeoutRef.current) {
        clearTimeout(bufferTimeoutRef.current);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (isListening) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isListening, handleKeyDown]);

  const startListening = () => {
    setIsListening(true);
    setInputBuffer('');
    setScannedData('');
    setBookInfo(null);
    inputRef.current?.focus();
  };

  const stopListening = () => {
    setIsListening(false);
    setInputBuffer('');
    if (bufferTimeoutRef.current) {
      clearTimeout(bufferTimeoutRef.current);
    }
  };

  const handleClose = () => {
    stopListening();
    onClose();
  };

  const handleSelectScannedData = (data: string) => {
    onScan(data);
    handleClose();
  };

  const handleSelectBookInfo = () => {
    if (bookInfo) {
      // Convert book info to JSON string for form processing with API flag
      const dataWithFlag = { ...bookInfo, isFromAPI: true };
      onScan(JSON.stringify(dataWithFlag));
      handleClose();
    }
  };

  const clearHistory = () => {
    setScanHistory([]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl mx-4">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              QR Code Scanner
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <Alert className="mb-4">
            <Keyboard className="h-4 w-4" />
            <AlertDescription>
              <strong>How to use:</strong> Point the scanner at the QR code and scan.
              If it holds an ISBN, the book's details are looked up for you.
            </AlertDescription>
          </Alert>

          {/* Hidden input to capture scanner data */}
          <div className="mb-4">
            <Label htmlFor="scanner-input" className="sr-only">
              Scanner Input
            </Label>
            <Input
              ref={inputRef}
              id="scanner-input"
              type="text"
              value={inputBuffer}
              onChange={(e) => setInputBuffer(e.target.value)}
              placeholder={isListening ? "Ready to scan... Point your scanner here" : "Click 'Start Scanning' to begin"}
              className="font-mono text-sm"
              readOnly
            />
          </div>

          {/* Status and Controls */}
          <div className="flex items-center gap-2 mb-4">
            <Badge variant={isListening ? "default" : "secondary"}>
              {isListening ? (
                <>
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Listening
                </>
              ) : (
                <>
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Not Listening
                </>
              )}
            </Badge>

            {isFetchingBook && (
              <Badge variant="secondary">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Fetching book info...
              </Badge>
            )}

            <div className="flex gap-2 ml-auto">
              {!isListening ? (
                <Button onClick={startListening} className="flex items-center gap-2">
                  <QrCode className="h-4 w-4" />
                  Start Scanning
                </Button>
              ) : (
                <Button variant="outline" onClick={stopListening} className="flex items-center gap-2">
                  <X className="h-4 w-4" />
                  Stop Scanning
                </Button>
              )}
            </div>
          </div>

          {/* Book Information Display */}
          {bookInfo && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-blue-800 flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Book Information Found
                </span>
                <Button
                  ref={useBookButtonRef}
                  size="sm"
                  onClick={handleSelectBookInfo}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Use This Book
                </Button>
              </div>

              <div className="space-y-2 text-sm">
                {bookInfo.title && (
                  <div><strong>Title:</strong> {bookInfo.title}</div>
                )}
                {bookInfo.subtitle && (
                  <div><strong>Subtitle:</strong> {bookInfo.subtitle}</div>
                )}
                {bookInfo.authors && bookInfo.authors.length > 0 && (
                  <div><strong>Authors:</strong> {bookInfo.authors.join(', ')}</div>
                )}
                {bookInfo.publisher && (
                  <div><strong>Publisher:</strong> {bookInfo.publisher}</div>
                )}
                {bookInfo.publication_date && (
                  <div><strong>Published:</strong> {bookInfo.publication_date}</div>
                )}
                {bookInfo.isbn && (
                  <div><strong>ISBN:</strong> {bookInfo.isbn}</div>
                )}
                {bookInfo.pages && (
                  <div><strong>Pages:</strong> {bookInfo.pages}</div>
                )}
                {bookInfo.language && (
                  <div><strong>Language:</strong> {bookInfo.language}</div>
                )}
              </div>
            </div>
          )}

          {/* Last Scanned Data (fallback) */}
          {scannedData && !bookInfo && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-green-800">Raw Scanned Data:</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleSelectScannedData(scannedData)}
                  className="text-green-700 border-green-300 hover:bg-green-100"
                >
                  Use Raw Data
                </Button>
              </div>
              <div className="font-mono text-sm text-green-700 bg-white p-2 rounded border">
                {scannedData}
              </div>
            </div>
          )}

          {/* Scan History */}
          {scanHistory.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Recent Scans:</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearHistory}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Clear History
                </Button>
              </div>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {scanHistory.map((scan, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded border cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSelectScannedData(scan)}
                  >
                    <span className="font-mono text-sm flex-1 truncate mr-2">
                      {scan}
                    </span>
                    <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-800">
                      Use
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
            <strong>Tips:</strong>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>A QR code with an ISBN in it is looked up automatically</li>
              <li>Details come from Open Library</li>
              <li>No ISBN in the code? The raw text is kept so you can still use it</li>
              <li>Try a simple code first to check the scanner works</li>
              <li>Press Enter or click "Use This Book" to confirm</li>
            </ul>
          </div>

          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
